import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, useClerk } from "@clerk/clerk-react";
import { Trash2 } from "lucide-react";
// The design team's own glyph, pulled in as a component so a className can size
// it. It draws its own stroke in the ink the row is written in, so there is no
// colour to keep in step with the label beside it.
import { ReactComponent as LogoutIcon } from "../../../assets/svg/setting/logout_icon.svg";
import { Button, SkeletonCard, useToast } from "../../../components/ui";
import { ROUTES } from "../../../routes/paths";
import { LogoutConfirmModal } from "../../auth/components/LogoutConfirmModal";
import { useSignedInUser } from "../../auth/hooks/useSignedInUser";
import {
  deleteAccount,
  deletionFailureMessage,
} from "../services/accountService";
import DeleteAccountModal from "./DeleteAccountModal";

const Card: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className = "",
}) => (
  <section className={`card-surface border border-line bg-white ${className}`}>
    {children}
  </section>
);

/**
 * Settings → Accounts: the address the account is known by, the way out of it,
 * and the way to end it.
 *
 * Three things, in that order, and the order is the design's point — what is
 * merely informative sits at the top, and what cannot be undone sits alone at
 * the foot of the screen with a rule around it.
 */
const AccountsTab: React.FC = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const { signOut } = useClerk();
  const { email, isLoaded } = useSignedInUser();

  const [confirmingLogout, setConfirmingLogout] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  /**
   * Ends the session and leaves for the sign-up screen.
   *
   * Sign-up rather than login, unlike an ordinary logout: there is nothing left
   * to log in to, and offering a form for credentials that no longer match an
   * account reads as a failure on the user's part. Starting again is the only
   * thing still open to them here.
   *
   * The account is already gone by the time this runs, so a sign-out that
   * fails changes nothing about that — the session it was clearing has no
   * account behind it. The navigation happens either way rather than leaving
   * somebody sitting on a workspace that no longer exists.
   */
  const leaveForSignup = async () => {
    try {
      await signOut(() => navigate(ROUTES.signup, { replace: true }));
    } catch (cause) {
      console.error("sign-out after account deletion failed:", cause);
      navigate(ROUTES.signup, { replace: true });
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      // The backend's own words, not ours: it is what knows the deletion
      // actually happened, and there is no record left to report instead. The
      // toast is raised above the route table and so survives the navigation
      // below — see `App`.
      const message = await deleteAccount(getToken);
      toast.success("Account deleted", message);
    } catch (cause) {
      console.error("account deletion failed:", cause);
      toast.error(
        "Could not delete your account",
        deletionFailureMessage(cause)
      );
      // The account is still there, so the dialog stays open and armed for
      // another try.
      setDeleting(false);
      return;
    }

    // Past here the account is gone, which is why leaving is not inside the
    // try: nothing that happens on the way out can turn a deletion that
    // landed back into one to apologise for.
    //
    // The dialog is left standing until the navigation does. Closing it first
    // would flash the workspace — clients, plans, revenue — behind it, which
    // is the one thing that is certainly no longer there.
    await leaveForSignup();
  };

  return (
    // Full height, so the danger zone can sit at the foot of the screen rather
    // than under the two rows above it — the same contract the billing section
    // has, and the same reason: distance is what keeps it from being pressed on
    // the way past.
    <div className="flex flex-1 flex-col gap-4">
      <Card>
        {/* Label over the address on a phone, beside it from `md` up. An email
            is the one value on this tab that cannot be shortened — it breaks
            mid-word rather than wrap — and set beside its label on a 360px card
            it is left three or four characters to a line. Over it, it gets the
            whole width. */}
        <div className="flex flex-col gap-1 px-5 py-4 md:flex-row md:flex-wrap md:items-baseline md:gap-x-2 md:gap-y-1 3xl:px-6 3xl:py-5">
          <span className="text-[14px] 3xl:text-[15px] text-ink-muted">
            Registered email:
          </span>
          {/* Clerk holds the address and has it in memory, so this costs no
              request — but there is a moment before the session is restored
              where nothing is known, and a blank line beside the label would
              read as an account with no email on it. */}
          {isLoaded ? (
            <span className="min-w-0 break-all text-[14px] 3xl:text-[15px] font-medium text-ink">
              {email || "-"}
            </span>
          ) : (
            <SkeletonCard className="h-4 w-48" />
          )}
        </div>
      </Card>

      <Card>
        <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3.5 3xl:px-6 3xl:py-4">
          <span className="flex items-center gap-2.5 text-[14px] 3xl:text-[15px] text-ink">
            <LogoutIcon
              aria-hidden="true"
              className="h-[18px] w-[18px] flex-shrink-0"
            />
            Logout
          </span>

          <Button variant="secondary" size="md" onClick={() => setConfirmingLogout(true)}>
            Logout
          </Button>
        </div>
      </Card>

      {/* Pushed to the foot of the screen and pinned there once the page is
          longer than the window, the same way the billing section's refund bar
          is. The tint is the page's own fill, so anything above passes behind
          it rather than through it; the negative margin takes back the space
          its padding adds, so its resting place is the foot of the page. */}
      <div className="sticky bottom-0 z-10 -mb-5 mt-auto bg-[#FCFCFD] pb-5 pt-4 3xl:-mb-8 3xl:pb-8">
        <section
          aria-labelledby="danger-zone-heading"
          className="card-surface border border-[#FF3B30] bg-white"
        >
          <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 3xl:px-6 3xl:py-5">
            <div className="min-w-0">
              <h2
                id="danger-zone-heading"
                className="text-[15px] 3xl:text-[16px] font-medium text-[#FF3B30]"
              >
                Danger zone
              </h2>
              <p className="mt-1 text-[13px] 3xl:text-[14px] text-ink-muted">
                Permanently remove your account, clients, plans, and revenue
                history. This can&rsquo;t be undone.
              </p>
            </div>

            {/* Filled rather than outlined, unlike the refund card's: that one
                asks for money back and this one ends everything, and the two
                should not look like the same size of decision.

                Across the full width on a phone, where the paragraph above it
                takes the whole card and leaves it wrapped onto a line of its
                own: a stranded button at the left edge reads as an afterthought
                rather than as the card's one action. */}
            <Button
              variant="danger"
              size="md"
              startIcon={<Trash2 aria-hidden="true" className="h-4 w-4" />}
              onClick={() => setConfirmingDelete(true)}
              className="w-full md:w-auto"
            >
              Delete account
            </Button>
          </div>
        </section>
      </div>

      <LogoutConfirmModal
        open={confirmingLogout}
        onClose={() => setConfirmingLogout(false)}
      />

      <DeleteAccountModal
        open={confirmingDelete}
        deleting={deleting}
        onClose={() => setConfirmingDelete(false)}
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default AccountsTab;
